import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/auth'
import Layout, { StatusBadge } from '../components/Layout'

const API = import.meta.env.VITE_API as string
const money = (n: number | null) => (n ?? 0).toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' })
const date = (d: string | null) => (d ? new Date(d).toLocaleDateString('fr-CA') : '—')
const fileSize = (b: number) => b > 1e9 ? (b / 1e9).toFixed(1) + ' Go' : b > 1e6 ? (b / 1e6).toFixed(1) + ' Mo' : Math.max(1, Math.round(b / 1e3)) + ' Ko'

interface Deliverable { name: string; size: number; modified: string }

interface Project { id: string; title: string | null; status: string | null; description: string | null; delivery_date: string | null }
interface Video { id: string; project_id: string; title: string | null; review_status: string | null; created_at: string }
interface Invoice { id: string; amount: number | null; status: string | null; due_date: string | null; invoice_number: string | null }
interface Contract { id: string; title: string | null; sent_at: string | null; viewed_at: string | null; file_path: string | null; filename: string | null }

export default function Portail() {
  const { profile } = useAuth()
  const [loading, setLoading] = useState(true)
  const [company, setCompany] = useState<string | null>(null)
  const [projects, setProjects] = useState<Project[]>([])
  const [videos, setVideos] = useState<Video[]>([])
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [contracts, setContracts] = useState<Contract[]>([])
  const [files, setFiles] = useState<Deliverable[]>([])
  const [slug, setSlug] = useState<string | null>(null)
  const [jwt, setJwt] = useState<string>('')

  useEffect(() => {
    (async () => {
      const { data: u } = await supabase.auth.getUser()
      const uid = u.user?.id
      if (!uid) { setLoading(false); return }
      const { data: client } = await supabase.from('clients').select('id,company,full_name').eq('profile_id', uid).maybeSingle()
      setCompany(client?.company || client?.full_name || null)
      const cid = client?.id
      const [p, v, i, c] = await Promise.all([
        cid ? supabase.from('projects').select('id,title,status,description,delivery_date').eq('client_id', cid).order('created_at', { ascending: false }) : Promise.resolve({ data: [] as any }),
        cid ? supabase.from('videos').select('id,project_id,title,review_status,created_at').eq('client_id', cid).order('created_at', { ascending: false }) : Promise.resolve({ data: [] as any }),
        cid ? supabase.from('invoices').select('id,amount,status,due_date,invoice_number').eq('client_id', cid).order('created_at', { ascending: false }) : Promise.resolve({ data: [] as any }),
        cid ? supabase.from('contracts').select('id,title,sent_at,viewed_at,file_path,filename').eq('client_id', cid).order('created_at', { ascending: false }) : Promise.resolve({ data: [] as any }),
      ])
      setProjects((p.data as any) || []); setVideos((v.data as any) || [])
      setInvoices((i.data as any) || []); setContracts((c.data as any) || [])
      // Livrables sur disque (via API authentifiée)
      const { data: sess } = await supabase.auth.getSession()
      const token = sess.session?.access_token || ''
      setJwt(token)
      try {
        const r = await fetch(`${API}/deliverables`, { headers: { Authorization: `Bearer ${token}` } })
        const j = await r.json()
        setFiles(j.files || []); setSlug(j.slug)
      } catch { /* API offline en dev */ }
      setLoading(false)
    })()
  }, [])

  const contractHref = (c: Contract) => c.file_path ? `${API}/deliverable?slug=${c.file_path.split('/')[0]}&name=${encodeURIComponent(c.file_path.split('/').slice(1).join('/'))}&access_token=${jwt}` : '#'
  const markViewed = (c: Contract) => { if (!c.viewed_at) { supabase.from('contracts').update({ viewed_at: new Date().toISOString() }).eq('id', c.id).then(() => setContracts(cs => cs.map(x => x.id === c.id ? { ...x, viewed_at: new Date().toISOString() } : x))) } }

  return (
    <Layout title={`Bonjour${company ? ', ' + company : ''}`}>
      {loading ? (
        <p className="text-acier">Chargement de votre espace…</p>
      ) : (
        <div className="space-y-10">
          {/* Projets */}
          <section>
            <h2 className="font-display text-2xl tracking-wide mb-4">Mes projets</h2>
            {projects.length === 0 ? (
              <p className="text-acier text-sm">Aucun projet pour le moment. Notre équipe vous contactera bientôt.</p>
            ) : (
              <div className="space-y-4">
                {projects.map(p => (
                  <div key={p.id} className="border border-white/10 rounded p-5 bg-white/[0.02]">
                    <div className="flex items-center justify-between">
                      <h3 className="font-display text-xl tracking-wide">{p.title || 'Projet'}</h3>
                      <StatusBadge status={p.status} />
                    </div>
                    {p.description && <p className="text-acier text-sm mt-2">{p.description}</p>}
                    <p className="text-xs text-acier mt-2">Livraison prévue : {date(p.delivery_date)}</p>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Vidéos à réviser */}
          {videos.length > 0 && (
            <section>
              <h2 className="font-display text-2xl tracking-wide mb-4">Vidéos à réviser</h2>
              <div className="space-y-2">
                {videos.map(v => (
                  <a key={v.id} href={`/revue/${v.id}`} className="flex items-center justify-between border border-white/10 rounded px-4 py-3 text-sm hover:border-orange transition-colors">
                    <span>🎬 {v.title || 'Vidéo'}</span>
                    <span className="flex items-center gap-3"><StatusBadge status={v.review_status} /><span className="text-orange text-xs uppercase tracking-widest">Voir & commenter →</span></span>
                  </a>
                ))}
              </div>
            </section>
          )}

          {/* Livrables / fichiers */}
          {files.length > 0 && (
            <section>
              <h2 className="font-display text-2xl tracking-wide mb-4">Fichiers & livrables</h2>
              <div className="space-y-2">
                {files.map(f => {
                  const isVideo = /\.(mp4|mov|webm|m4v)$/i.test(f.name)
                  const href = `${API}/deliverable?slug=${slug}&name=${encodeURIComponent(f.name)}&access_token=${jwt}`
                  return (
                    <div key={f.name} className="flex items-center justify-between border border-white/10 rounded px-4 py-3 text-sm">
                      <span className="truncate">{isVideo ? '🎬' : /\.(jpe?g|png|gif|webp|heic|avif)$/i.test(f.name) ? '🖼️' : '📎'} {f.name} <span className="text-acier text-xs">· {fileSize(f.size)}</span></span>
                      <a href={href} target="_blank" rel="noreferrer" className="text-orange hover:underline text-xs uppercase tracking-widest whitespace-nowrap ml-3">
                        {isVideo ? 'Voir' : 'Télécharger'}
                      </a>
                    </div>
                  )
                })}
              </div>
            </section>
          )}

          {/* Factures */}
          {invoices.length > 0 && (
            <section>
              <h2 className="font-display text-2xl tracking-wide mb-4">Factures</h2>
              <div className="space-y-2">
                {invoices.map(f => (
                  <a key={f.id} href={`/factures/${f.id}`} target="_blank" rel="noreferrer" className="flex items-center justify-between border border-white/10 rounded px-4 py-3 text-sm hover:border-orange transition-colors">
                    <span>{f.invoice_number || 'Facture'} · échéance {date(f.due_date)}</span>
                    <span className="flex items-center gap-3">{money(f.amount)} <StatusBadge status={f.status} /><span className="text-orange text-xs uppercase tracking-widest">PDF</span></span>
                  </a>
                ))}
              </div>
            </section>
          )}

          {/* Contrats */}
          {contracts.length > 0 && (
            <section>
              <h2 className="font-display text-2xl tracking-wide mb-4">Contrats</h2>
              <div className="space-y-2">
                {contracts.map(c => (
                  <a key={c.id} href={contractHref(c)} target="_blank" rel="noreferrer" onClick={() => markViewed(c)} className="flex items-center justify-between border border-white/10 rounded px-4 py-3 text-sm hover:border-orange transition-colors">
                    <span>📄 {c.title || 'Contrat'}</span>
                    <span className="flex items-center gap-3"><StatusBadge status={c.viewed_at ? 'vu' : c.sent_at ? 'envoyé' : 'brouillon'} /><span className="text-orange text-xs uppercase tracking-widest">Ouvrir</span></span>
                  </a>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </Layout>
  )
}
